/**
 * Agent-specific visual previews for the Workshop.
 *
 * Each renderer takes a task and produces a stylized mockup of the deliverable
 * so the founder can SEE what the agent will ship — before execution.
 *
 *  · AriaPreview    → IDE/code preview with browser chrome
 *  · HermesPreview  → mini-CRM lead cards
 *  · LyraPreview    → social post mockup (Instagram-flavored)
 *  · MarkusPreview  → email composer + phone outreach mock
 */

import React from 'react';
import {
  Code2, Globe, Sparkles, Heart, MessageCircle, Send,
  Mail, Phone, Building2, ChevronRight, Loader2, Eye,
} from 'lucide-react';

/* ============================== Aria ============================== */
export function AriaPreview({ task }) {
  const title = task?.title || 'Untitled build';
  const description = task?.description || 'Aria will ship this component.';
  const tool = (task?.tool || task?.params?.tool || 'react').toString();

  return (
    <div data-testid="workshop-preview-aria" style={{
      borderRadius: 14,
      overflow: 'hidden',
      border: '1px solid rgba(124,58,237,0.22)',
      background: '#0F0B1F',
      boxShadow: '0 24px 60px -20px rgba(124,58,237,0.5)',
    }}>
      {/* Browser chrome */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '11px 16px',
        background: 'linear-gradient(180deg, #1B1233 0%, #16102B 100%)',
        borderBottom: '1px solid rgba(124,58,237,0.2)',
      }}>
        <div style={{ display: 'flex', gap: 6 }}>
          <span style={{ width: 11, height: 11, borderRadius: '50%', background: '#FF5F57' }} />
          <span style={{ width: 11, height: 11, borderRadius: '50%', background: '#FEBC2E' }} />
          <span style={{ width: 11, height: 11, borderRadius: '50%', background: '#28C840' }} />
        </div>
        <div style={{
          flex: 1, height: 24, borderRadius: 6,
          background: 'rgba(255,255,255,0.05)',
          border: '1px solid rgba(255,255,255,0.08)',
          display: 'flex', alignItems: 'center', gap: 7, padding: '0 11px',
          fontSize: 11, color: 'rgba(255,255,255,0.55)',
          fontFamily: "'JetBrains Mono', monospace",
        }}>
          <Globe size={10} /> localhost:3000 / <span style={{ color: '#A78BFA' }}>preview</span>
        </div>
        <span style={{
          fontSize: 9, fontWeight: 800, letterSpacing: '0.18em', textTransform: 'uppercase',
          color: '#A78BFA', padding: '3px 8px',
          background: 'rgba(124,58,237,0.15)',
          border: '1px solid rgba(124,58,237,0.35)',
          borderRadius: 999,
        }}>BUILD</span>
      </div>

      {/* Code surface */}
      <div style={{
        display: 'grid', gridTemplateColumns: '34px 1fr',
        minHeight: 240,
        background: 'radial-gradient(ellipse at top, rgba(124,58,237,0.12) 0%, transparent 60%), #0A0717',
      }}>
        <div style={{
          background: 'rgba(255,255,255,0.02)',
          borderRight: '1px solid rgba(255,255,255,0.04)',
          padding: '14px 0',
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: 10, color: 'rgba(255,255,255,0.22)',
          textAlign: 'right',
          lineHeight: '17px',
        }}>
          {Array.from({ length: 12 }, (_, i) => (
            <div key={i} style={{ paddingRight: 8 }}>{i + 1}</div>
          ))}
        </div>
        <div style={{
          padding: '14px 18px',
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: 11, lineHeight: '17px',
          color: 'rgba(255,255,255,0.85)',
        }}>
          <div><span style={{ color: '#C792EA' }}>export default function</span> <span style={{ color: '#82AAFF' }}>Component</span>() {'{'}
          </div>
          <div style={{ paddingLeft: 18, color: 'rgba(255,255,255,0.55)' }}>
            <span style={{ color: '#7C7C8A' }}>// {title}</span>
          </div>
          <div style={{ paddingLeft: 18 }}>
            <span style={{ color: '#C792EA' }}>return</span> (
          </div>
          <div style={{ paddingLeft: 36 }}>
            &lt;<span style={{ color: '#F78C6C' }}>section</span>{' '}<span style={{ color: '#FFCB6B' }}>className</span>=<span style={{ color: '#C3E88D' }}>&quot;hero&quot;</span>&gt;
          </div>
          <div style={{ paddingLeft: 54 }}>
            &lt;<span style={{ color: '#F78C6C' }}>h1</span>&gt;<span style={{ color: '#A78BFA' }}>{title.slice(0, 42)}</span>&lt;/<span style={{ color: '#F78C6C' }}>h1</span>&gt;
          </div>
          <div style={{ paddingLeft: 54 }}>
            &lt;<span style={{ color: '#F78C6C' }}>p</span>&gt;{description.slice(0, 70)}…&lt;/<span style={{ color: '#F78C6C' }}>p</span>&gt;
          </div>
          <div style={{ paddingLeft: 36 }}>
            &lt;/<span style={{ color: '#F78C6C' }}>section</span>&gt;
          </div>
          <div style={{ paddingLeft: 18 }}>);</div>
          <div>{'}'}</div>
        </div>
      </div>

      {/* Footer meta */}
      <div style={{
        padding: '10px 14px',
        background: '#13091F',
        borderTop: '1px solid rgba(124,58,237,0.18)',
        display: 'flex', alignItems: 'center', gap: 10,
        fontSize: 10, color: 'rgba(255,255,255,0.55)',
        fontFamily: "'JetBrains Mono', monospace",
      }}>
        <Code2 size={11} color="#A78BFA" />
        <span>Tool · <span style={{ color: '#A78BFA' }}>{tool}</span></span>
        <span style={{ color: 'rgba(255,255,255,0.25)' }}>·</span>
        <span>ETA · <span style={{ color: '#fff' }}>{task?.eta_minutes ?? 25}m</span></span>
        <span style={{ flex: 1 }} />
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 5,
          color: '#22C55E',
        }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#22C55E' }} />
          preview ready
        </span>
      </div>
    </div>
  );
}

/* ============================== Lyra ============================== */
export function LyraPreview({ task }) {
  const caption = task?.title || 'A post you didn\'t know you needed.';
  const description = task?.description || 'Aesthetic, on-brand, ready to publish.';
  const handle = '@bornto.bealive';

  return (
    <div data-testid="workshop-preview-lyra" style={{
      display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14,
    }}>
      {/* Instagram-style card */}
      <div style={{
        borderRadius: 16, overflow: 'hidden',
        background: '#FFFFFF',
        border: '1px solid rgba(15,15,30,0.08)',
        boxShadow: '0 20px 50px -20px rgba(255,107,26,0.35)',
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
          borderBottom: '1px solid rgba(15,15,30,0.06)',
        }}>
          <div style={{
            width: 32, height: 32, borderRadius: '50%',
            padding: 2,
            background: 'conic-gradient(from 180deg, #FF6B1A, #FFD166, #C026D3, #FF6B1A)',
          }}>
            <div style={{
              width: '100%', height: '100%', borderRadius: '50%',
              background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 13, fontWeight: 900, color: '#FF6B1A',
            }}>L</div>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: '#0F0F1A' }}>{handle}</div>
            <div style={{ fontSize: 10, color: 'rgba(15,15,30,0.55)' }}>Sponsored · just now</div>
          </div>
          <div style={{ fontSize: 18, color: 'rgba(15,15,30,0.45)' }}>•••</div>
        </div>
        <div style={{
          aspectRatio: '1 / 1', position: 'relative',
          background: 'linear-gradient(135deg, #FFD166 0%, #FF6B1A 50%, #C026D3 100%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 22,
        }}>
          <div style={{
            position: 'absolute', inset: 14,
            border: '1px dashed rgba(255,255,255,0.45)',
            borderRadius: 12,
          }} />
          <div style={{
            position: 'relative', textAlign: 'center',
            color: '#FFFFFF', fontWeight: 900,
            fontSize: 22, letterSpacing: '-0.02em',
            textShadow: '0 4px 24px rgba(0,0,0,0.3)',
            lineHeight: 1.15,
            maxWidth: 220,
          }}>
            {caption.slice(0, 50)}
          </div>
          <div style={{
            position: 'absolute', bottom: 14, left: 14, right: 14,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            fontSize: 10, fontWeight: 700,
            color: 'rgba(255,255,255,0.85)',
            letterSpacing: '0.16em', textTransform: 'uppercase',
          }}>
            <span>visual concept</span>
            <span>1080 × 1080</span>
          </div>
        </div>
        <div style={{ padding: '8px 12px 12px' }}>
          <div style={{ display: 'flex', gap: 14, marginBottom: 8 }}>
            <Heart size={20} color="#0F0F1A" />
            <MessageCircle size={20} color="#0F0F1A" />
            <Send size={20} color="#0F0F1A" />
          </div>
          <div style={{ fontSize: 11, color: '#0F0F1A', lineHeight: 1.4 }}>
            <strong>{handle}</strong> {description.slice(0, 110)}
          </div>
        </div>
      </div>

      {/* Caption / hashtags side */}
      <div style={{
        borderRadius: 16, padding: 16,
        background: 'linear-gradient(180deg, #FFF6E6 0%, #FFEED4 100%)',
        border: '1px solid rgba(255,107,26,0.2)',
        display: 'flex', flexDirection: 'column', gap: 10,
      }}>
        <div style={{
          fontSize: 9, fontWeight: 800, letterSpacing: '0.22em', textTransform: 'uppercase',
          color: '#FF6B1A',
          display: 'flex', alignItems: 'center', gap: 6,
        }}>
          <Sparkles size={11} /> Caption draft
        </div>
        <div style={{
          fontSize: 12, lineHeight: 1.55, color: '#1A0F00', fontWeight: 500,
        }}>
          {caption}. {description}
        </div>
        <div style={{
          fontSize: 10, color: '#C04200', fontFamily: "'JetBrains Mono', monospace",
        }}>
          #buildinpublic #aifounder #ventureos #BornToBeAlive
        </div>
        <div style={{
          marginTop: 'auto', display: 'flex', alignItems: 'center', gap: 8,
          fontSize: 10, color: 'rgba(15,15,30,0.55)',
        }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#FF6B1A' }} />
          Ready for Canva export
        </div>
      </div>
    </div>
  );
}

/* ============================== Hermes ============================== */
export function HermesPreview({ task }) {
  const segment = task?.params?.segment || task?.title || 'qualified leads';
  // Procedurally render 3 mock lead cards.
  const sample = [
    { name: 'Acme Robotics', size: '32 ppl', vertical: 'Hardware AI', score: 92, signal: 'Series A · just closed' },
    { name: 'Northwind Labs', size: '11 ppl', vertical: 'Health SaaS', score: 87, signal: 'Hiring growth lead' },
    { name: 'Solstice IO', size: '6 ppl', vertical: 'Dev tools', score: 81, signal: 'Recent blog on automation' },
  ];

  return (
    <div data-testid="workshop-preview-hermes" style={{
      borderRadius: 16,
      background: 'linear-gradient(180deg, #FFFBE6 0%, #FFF4C2 100%)',
      border: '1px solid rgba(255,209,102,0.45)',
      padding: 16,
      boxShadow: '0 20px 50px -20px rgba(255,179,71,0.45)',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12,
      }}>
        <div style={{
          fontSize: 9, fontWeight: 800, letterSpacing: '0.22em', textTransform: 'uppercase',
          color: '#AC6A00', padding: '4px 9px',
          background: 'rgba(255,209,102,0.4)', borderRadius: 999,
        }}>
          Lead Lane · {segment.slice(0, 28)}
        </div>
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: 10, color: '#8A5400', fontFamily: "'JetBrains Mono', monospace" }}>
          3 / {task?.params?.target ?? 25}
        </span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {sample.map((lead, idx) => (
          <div key={idx} style={{
            display: 'grid', gridTemplateColumns: '40px 1fr auto',
            gap: 12, alignItems: 'center',
            padding: '12px 14px',
            background: '#FFFFFF',
            borderRadius: 12,
            border: '1px solid rgba(255,209,102,0.45)',
            boxShadow: '0 2px 8px rgba(172,106,0,0.06)',
          }}>
            <div style={{
              width: 40, height: 40, borderRadius: 10,
              background: 'linear-gradient(135deg, #FFE89D, #FFB347)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#7A4800', fontWeight: 900, fontSize: 14,
            }}>
              <Building2 size={18} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{
                fontSize: 13, fontWeight: 800, color: '#0F0F1A',
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}>
                {lead.name}
              </div>
              <div style={{
                fontSize: 10, color: 'rgba(15,15,30,0.55)',
                display: 'flex', alignItems: 'center', gap: 7, marginTop: 2,
              }}>
                <span>{lead.vertical}</span>
                <span style={{ width: 2, height: 2, borderRadius: '50%', background: 'rgba(15,15,30,0.25)' }} />
                <span>{lead.size}</span>
                <span style={{ width: 2, height: 2, borderRadius: '50%', background: 'rgba(15,15,30,0.25)' }} />
                <span style={{ color: '#AC6A00', fontWeight: 700 }}>{lead.signal}</span>
              </div>
            </div>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 9,
            }}>
              <div style={{
                fontSize: 11, fontWeight: 900,
                padding: '4px 8px', borderRadius: 6,
                background: lead.score > 90 ? '#22C55E22' : '#FFD16622',
                color: lead.score > 90 ? '#15803D' : '#AC6A00',
                fontFamily: "'JetBrains Mono', monospace",
              }}>
                {lead.score}
              </div>
              <ChevronRight size={14} color="rgba(15,15,30,0.4)" />
            </div>
          </div>
        ))}
      </div>

      <div style={{
        marginTop: 12, padding: '10px 12px',
        borderRadius: 10,
        background: 'rgba(255,255,255,0.5)',
        border: '1px dashed rgba(172,106,0,0.3)',
        fontSize: 11, color: '#7A4800',
        display: 'flex', alignItems: 'center', gap: 8,
      }}>
        <Loader2 size={12} />
        Hermes will pull {task?.params?.target ?? 25} more matching this signal via LeadMagic.
      </div>
    </div>
  );
}

/* ============================== Markus ============================== */
export function MarkusPreview({ task }) {
  const subject = task?.params?.subject || task?.title || 'Quick question about your roadmap';
  const recipient = task?.params?.to || 'first.last@company.com';
  const body = task?.description || `Hi {{first_name}},\n\nNoticed your team just shipped X — wanted to share a 60s loom on how we'd accelerate Y for you.\n\nWorth a 15-min next week?`;

  return (
    <div data-testid="workshop-preview-markus" style={{
      display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 14,
    }}>
      {/* Email composer */}
      <div style={{
        borderRadius: 14, overflow: 'hidden',
        background: '#FFFFFF',
        border: '1px solid rgba(192,38,211,0.22)',
        boxShadow: '0 24px 60px -24px rgba(192,38,211,0.5)',
      }}>
        <div style={{
          padding: '10px 14px',
          background: 'linear-gradient(180deg, #FAE8FF 0%, #F0AAFE 100%)',
          borderBottom: '1px solid rgba(192,38,211,0.25)',
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <Mail size={14} color="#86198F" />
          <div style={{
            fontSize: 11, fontWeight: 800, color: '#86198F',
            letterSpacing: '0.16em', textTransform: 'uppercase',
          }}>
            new outreach · draft
          </div>
          <span style={{ flex: 1 }} />
          <span style={{ fontSize: 10, color: '#86198F', fontFamily: "'JetBrains Mono', monospace" }}>
            via Resend
          </span>
        </div>

        <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <Row label="To" value={recipient} mono />
          <Row label="From" value="markus@yourdomain.com" mono />
          <Row label="Subject" value={subject} bold />
          <div style={{ height: 1, background: 'rgba(15,15,30,0.06)', margin: '4px 0' }} />
          <div style={{
            fontSize: 12.5, lineHeight: 1.6, color: '#0F0F1A',
            whiteSpace: 'pre-wrap', minHeight: 110,
          }}>
            {body}
          </div>
          <div style={{ height: 1, background: 'rgba(15,15,30,0.06)', margin: '6px 0 2px' }} />
          <div style={{
            fontSize: 11, color: 'rgba(15,15,30,0.6)',
            fontFamily: 'Georgia, serif', fontStyle: 'italic',
          }}>
            — Markus, on behalf of the founder
          </div>
        </div>

        <div style={{
          padding: '10px 14px',
          borderTop: '1px solid rgba(15,15,30,0.06)',
          background: '#FFFBFE',
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <button style={{
            padding: '7px 14px',
            background: 'linear-gradient(135deg, #E879F9 0%, #C026D3 100%)',
            color: '#FFFFFF',
            border: 'none', borderRadius: 999,
            fontSize: 11, fontWeight: 800, letterSpacing: '0.06em',
            fontFamily: 'inherit',
            display: 'inline-flex', alignItems: 'center', gap: 6,
            boxShadow: '0 6px 16px rgba(192,38,211,0.35)',
            cursor: 'default',
          }}>
            <Send size={11} /> Send when promoted
          </button>
          <span style={{ fontSize: 10, color: 'rgba(15,15,30,0.55)' }}>
            Tracked open + reply detection
          </span>
        </div>
      </div>

      {/* Phone call mock */}
      <div style={{
        borderRadius: 14,
        background: 'linear-gradient(180deg, #2A0735 0%, #1A0524 100%)',
        border: '1px solid rgba(232,121,249,0.22)',
        padding: 16,
        color: '#FFFFFF',
        display: 'flex', flexDirection: 'column',
      }}>
        <div style={{
          fontSize: 9, fontWeight: 800, letterSpacing: '0.22em', textTransform: 'uppercase',
          color: '#E879F9',
          display: 'flex', alignItems: 'center', gap: 6, marginBottom: 14,
        }}>
          <Phone size={11} /> follow-up call · queued
        </div>

        <div style={{
          display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14,
        }}>
          <div style={{
            width: 52, height: 52, borderRadius: '50%',
            background: 'linear-gradient(135deg, #E879F9 0%, #C026D3 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 900, fontSize: 17, color: '#FFFFFF',
            boxShadow: '0 0 30px rgba(232,121,249,0.55)',
          }}>
            {recipient.slice(0, 1).toUpperCase()}
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 800 }}>{recipient.split('@')[0]}</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', marginTop: 2 }}>
              {recipient.split('@')[1] || 'company.com'}
            </div>
          </div>
        </div>

        <div style={{
          padding: 12,
          background: 'rgba(232,121,249,0.08)',
          border: '1px solid rgba(232,121,249,0.2)',
          borderRadius: 10,
          fontSize: 11, color: 'rgba(255,255,255,0.78)', lineHeight: 1.5,
        }}>
          <div style={{
            fontSize: 9, fontWeight: 800, color: '#F0ABFC',
            letterSpacing: '0.18em', textTransform: 'uppercase', marginBottom: 6,
          }}>
            Talking points
          </div>
          1. Open w/ recent shipping signal<br />
          2. Pitch outcome (not feature)<br />
          3. Book demo · Tue/Thu 4pm
        </div>

        <div style={{ flex: 1 }} />

        <div style={{
          marginTop: 14, fontSize: 10,
          color: 'rgba(255,255,255,0.5)',
          fontFamily: "'JetBrains Mono', monospace",
          display: 'flex', alignItems: 'center', gap: 7,
        }}>
          <span style={{
            width: 6, height: 6, borderRadius: '50%', background: '#E879F9',
            animation: 'pulse-glow 1.4s ease-in-out infinite',
          }} />
          ETA · {task?.eta_minutes ?? 8}m · via Twilio
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, mono, bold }) {
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: '64px 1fr',
      gap: 10, alignItems: 'baseline',
    }}>
      <span style={{
        fontSize: 10, fontWeight: 700, color: 'rgba(15,15,30,0.5)',
        letterSpacing: '0.1em', textTransform: 'uppercase',
      }}>
        {label}
      </span>
      <span style={{
        fontSize: bold ? 14 : 12.5,
        fontWeight: bold ? 800 : 500,
        color: '#0F0F1A',
        fontFamily: mono ? "'JetBrains Mono', monospace" : 'inherit',
      }}>
        {value}
      </span>
    </div>
  );
}

/* =========================== Empty state =========================== */
export function EmptyPreview({ agent }) {
  return (
    <div data-testid="workshop-preview-empty" style={{
      minHeight: 280,
      border: `1px dashed ${agent?.accent || '#7C3AED'}55`,
      borderRadius: 14,
      background: `linear-gradient(180deg, ${agent?.accent || '#7C3AED'}06 0%, transparent 100%)`,
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      gap: 10, padding: 32,
    }}>
      <div style={{
        width: 56, height: 56, borderRadius: '50%',
        background: agent?.gradient || 'linear-gradient(135deg, #A78BFA, #7C3AED)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: `0 16px 36px ${agent?.accent || '#7C3AED'}40`,
      }}>
        <Eye size={22} color="#FFFFFF" />
      </div>
      <div style={{
        fontSize: 14, fontWeight: 800, color: '#0F0F1A', letterSpacing: '-0.01em',
      }}>
        Pick a task to preview it live
      </div>
      <div style={{
        fontSize: 12, color: 'rgba(15,15,30,0.55)', textAlign: 'center', maxWidth: 360,
        lineHeight: 1.55,
      }}>
        Every task {agent?.name || 'this IA'} planned can be shaped right here — refine
        the title, sharpen the body, then promote it to execution.
      </div>
    </div>
  );
}
